import { useState } from "react";

import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";

import { useDashboardStore } from "@/stores/useDashboardStore";

import { NewsPayloadDto, selectableImageType } from "../..";
import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../api/resources";
import Dialog from "../../../../../../components/Dialog/Dialog";
import { DialogHeight } from "../../../../../../components/Dialog/types/enums";
import InputField from "../../../../../../components/InputField/InputField";
import RichTextField from "../../../../../../components/RichTextField/RichTextField";
import { ALERT_STATUS } from "../../../../../../context/alert/types";
import { inputFieldMarginBottom } from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useNewsFormInputs from "../../../../../../shared/hooks/useNewsFormInputs";
import useRefreshDashboardData from "../../../../../../shared/hooks/useRefreshDashboardData";
import useResponse from "../../../../../../shared/hooks/useResponse";
import ImageDropzone from "../../components/ImageDropzone/ImageDropzone";
import ReleaseNotesCheck from "../../components/ReleaseNotesCheck/ReleaseNotesCheck";
import { imageList } from "../../config";

interface CreateNewsDialogProps {
  isDialogOpen: boolean;
  onClose: (resetAllVal: any) => void;
}

const CreateNewsDialog = ({ isDialogOpen, onClose }: CreateNewsDialogProps) => {
  const { postData } = useAxiosPrivate();
  const { handleResponse, handleCommonError } = useResponse();
  const { refreshNewsData } = useRefreshDashboardData();
  const { t } = useTranslation();

  const { unpublishedReleaseNote, setNewsScrollOffset } = useDashboardStore();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] =
    useState<selectableImageType | null>(null);
  const [isReleaseNotes, setIsReleaseNotes] = useState<boolean>(false);

  const {
    titleInputError,
    validateTitleLength,
    handleChangeTitleValue,
    title,
    resetAllValues,
    isTitleValid,
    validateRichTextLength,
    isRichTextValid,
    handleChangeRichTextValue,
    richTextInputError,
    richTextValue,
    isRichTextFocused,
    handleFocus,
    setTitle,
    setTitleInputError
  } = useNewsFormInputs({ setSelectedImage, setIsReleaseNotes });

  const isPayloadReadyToConfirm =
    title &&
    isTitleValid &&
    Boolean(richTextValue) &&
    isRichTextValid &&
    !richTextInputError &&
    Boolean(selectedImage);

  const isReleaseNotePayloadReady =
    isReleaseNotes &&
    Boolean(richTextValue) &&
    isRichTextValid &&
    !richTextInputError;

  const handleSubmit = async () => {
    const payload: NewsPayloadDto = {
      title: title?.trim() ?? null,
      content: richTextValue?.trim(),
      imageName: selectedImage?.imageName ?? null,
      isReleaseNotes: isReleaseNotes
    };

    try {
      setIsLoading(true);
      await postData(ApiResources.NewsItems, payload).then(() => {
        refreshNewsData();
      });

      setNewsScrollOffset(0);
      handleResponse(ALERT_STATUS.Success, t("Alert##news created"));
    } catch (error) {
      handleCommonError(error, t);
    } finally {
      setIsLoading(false);
      onClose(resetAllValues);
    }
  };

  const handleToggleCheckbox = () => {
    setIsReleaseNotes(prev => !prev);
    if (!title && !isReleaseNotes) {
      setTitle(t("Dialog##release notes"));
      setTitleInputError(null);
    }
    if (!selectedImage && !isReleaseNotes) {
      setSelectedImage(imageList[1]);
    }
  };

  return (
    <>
      {isDialogOpen && (
        <Dialog
          data-testid="create-news-dialog"
          title={t("Dialog##create news")}
          description={t("Dialog##create news description")}
          extendToMaxWidth
          maxHeight={DialogHeight.MaxHeight}
          isTitleSeparator
          isActionsSeparator
          open={isDialogOpen}
          close={() => onClose(resetAllValues)}
          submit={handleSubmit}
          actions={
            <DialogActions
              onClose={() => onClose(resetAllValues)}
              isLoading={isLoading}
              isreadyToConfirm={
                isPayloadReadyToConfirm || isReleaseNotePayloadReady
              }
              confirmationButtonText={t("Button##create")}
              confirmationButtonTestId="create-news-button"
              cancelButtonText={t("Button##close")}
              cancelButtonTestId="close-create-news"
            />
          }
        >
          <ImageDropzone
            setSelectedImage={setSelectedImage}
            selectedImage={selectedImage}
            label={t("Dropzone##select image")}
            disabled={isLoading || isReleaseNotes}
          />
          <InputField
            id={uuidv7()}
            testId="create-news-title-input-field"
            size="medium"
            fullWidth
            required
            errorText={titleInputError}
            onBlur={validateTitleLength}
            onChange={handleChangeTitleValue}
            labelLeft={t("Dialog##title")}
            placeholder={t("Dialog##enter here")}
            customStyle={inputFieldMarginBottom}
            disabled={isLoading || isReleaseNotes}
            value={title ?? ""}
          />
          <RichTextField
            label={`${t("Dialog##description required")}*`}
            isRichTextFocused={isRichTextFocused}
            richTextInputError={richTextInputError}
            richTextValue={richTextValue}
            handleChangeRichTextValue={handleChangeRichTextValue}
            validateRichTextLength={validateRichTextLength}
            handleFocus={handleFocus}
            isLoading={isLoading}
          />
          <ReleaseNotesCheck
            onCheckboxChange={handleToggleCheckbox}
            isLoading={isLoading}
            unpublishedReleaseNote={unpublishedReleaseNote}
            isChecked={isReleaseNotes}
          />
        </Dialog>
      )}
    </>
  );
};

export default CreateNewsDialog;
